/**
 * /settings/pos/products — retail product catalogue for over-the-counter POS
 * sales. Flat catalogue: no stock tracking, no variants (spec §14 / Plan 7 M5
 * Task 11).
 *
 * Drag-reorder is deferred to v1.1 — position is edited as an integer input.
 *
 * Gate: the page shows a permission-denied banner when the GET returns 403
 * (caller lacks pos.session.read) or 402 (Starter tier — POS is Pro+). Both
 * states render the same banner since the user cannot act in either case.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { useConfirm } from '@/components/Confirm';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  createPosProduct,
  deletePosProduct,
  listPosProducts,
  updatePosProduct,
} from '@/lib/api';
import type { CreatePosProductInput, PosProduct, UpdatePosProductInput } from '@/lib/types';
import { Package, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

function majorToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100);
}

function minorToMajor(minor: number): string {
  return (minor / 100).toFixed(2);
}

/** Group products by category. Products with no category go under "Uncategorised". */
function groupByCategory(products: PosProduct[]): Map<string, PosProduct[]> {
  const map = new Map<string, PosProduct[]>();
  for (const p of products) {
    const key = p.category ?? 'Uncategorised';
    const group = map.get(key) ?? [];
    group.push(p);
    map.set(key, group);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Editor drawer
// ---------------------------------------------------------------------------

interface EditorFormState {
  sku: string;
  name: string;
  description: string;
  priceText: string; // major-currency string e.g. "19.99"
  category: string;
  taxable: boolean;
  active: boolean;
  positionText: string;
  // Stock tracking
  stockTrackingEnabled: boolean;
  stockQuantityText: string;
  reorderThresholdText: string;
  reorderQuantityText: string;
  supplierName: string;
  supplierSku: string;
  supplierUrl: string;
  costText: string;
}

function emptyForm(): EditorFormState {
  return {
    sku: '',
    name: '',
    description: '',
    priceText: '',
    category: '',
    taxable: true,
    active: true,
    positionText: '10',
    stockTrackingEnabled: false,
    stockQuantityText: '0',
    reorderThresholdText: '0',
    reorderQuantityText: '0',
    supplierName: '',
    supplierSku: '',
    supplierUrl: '',
    costText: '',
  };
}

function formFromProduct(p: PosProduct): EditorFormState {
  return {
    sku: p.sku ?? '',
    name: p.name,
    description: p.description ?? '',
    priceText: minorToMajor(p.priceMinor),
    category: p.category ?? '',
    taxable: p.taxable === 1,
    active: p.active === 1,
    positionText: String(p.position),
    stockTrackingEnabled: p.stockTrackingEnabled === 1,
    stockQuantityText: String(p.stockQuantity),
    reorderThresholdText: String(p.reorderThreshold),
    reorderQuantityText: String(p.reorderQuantity),
    supplierName: p.supplierName ?? '',
    supplierSku: p.supplierSku ?? '',
    supplierUrl: p.supplierUrl ?? '',
    costText: p.costMinor !== null ? minorToMajor(p.costMinor) : '',
  };
}

interface EditorDrawerProps {
  existing: PosProduct | null; // null = create
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}

function EditorDrawer({ existing, currency, onClose, onSaved }: EditorDrawerProps) {
  const [form, setForm] = useState<EditorFormState>(() =>
    existing ? formFromProduct(existing) : emptyForm(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<string[] | null>(null);

  const sym = currencySymbol(currency);

  async function handleSave() {
    setBusy(true);
    setError(null);
    setServerIssues(null);

    const nameTrimmed = form.name.trim();
    if (nameTrimmed.length === 0) {
      setError('Name is required');
      setBusy(false);
      return;
    }

    const priceMinor = majorToMinor(form.priceText);
    if (priceMinor === null) {
      setError('Price must be a positive number');
      setBusy(false);
      return;
    }

    const positionVal = Number(form.positionText);
    if (!Number.isInteger(positionVal) || positionVal < 0) {
      setError('Position must be a non-negative integer');
      setBusy(false);
      return;
    }

    const stockQuantityVal = Number(form.stockQuantityText);
    const reorderThresholdVal = Number(form.reorderThresholdText);
    const reorderQuantityVal = Number(form.reorderQuantityText);
    const costMinorVal =
      form.costText.trim() !== '' ? Math.round(Number(form.costText) * 100) : null;

    try {
      if (existing) {
        const patch: UpdatePosProductInput = {
          sku: form.sku.trim() === '' ? null : form.sku.trim(),
          name: nameTrimmed,
          description: form.description.trim() === '' ? null : form.description.trim(),
          price_minor: priceMinor,
          currency,
          taxable: form.taxable,
          category: form.category.trim() === '' ? null : form.category.trim(),
          active: form.active,
          position: positionVal,
          stock_tracking_enabled: form.stockTrackingEnabled,
          stock_quantity: Number.isFinite(stockQuantityVal) ? stockQuantityVal : 0,
          reorder_threshold:
            Number.isFinite(reorderThresholdVal) && reorderThresholdVal >= 0
              ? reorderThresholdVal
              : 0,
          reorder_quantity:
            Number.isFinite(reorderQuantityVal) && reorderQuantityVal >= 0 ? reorderQuantityVal : 0,
          supplier_name: form.supplierName.trim() || null,
          supplier_sku: form.supplierSku.trim() || null,
          supplier_url: form.supplierUrl.trim() || null,
          cost_minor: costMinorVal,
        };
        await updatePosProduct(existing.id, patch);
      } else {
        const input: CreatePosProductInput = {
          sku: form.sku.trim() === '' ? null : form.sku.trim(),
          name: nameTrimmed,
          description: form.description.trim() === '' ? null : form.description.trim(),
          price_minor: priceMinor,
          currency,
          taxable: form.taxable,
          category: form.category.trim() === '' ? null : form.category.trim(),
          active: form.active,
          position: positionVal,
          stock_tracking_enabled: form.stockTrackingEnabled,
          stock_quantity: Number.isFinite(stockQuantityVal) ? stockQuantityVal : 0,
          reorder_threshold:
            Number.isFinite(reorderThresholdVal) && reorderThresholdVal >= 0
              ? reorderThresholdVal
              : 0,
          reorder_quantity:
            Number.isFinite(reorderQuantityVal) && reorderQuantityVal >= 0 ? reorderQuantityVal : 0,
          supplier_name: form.supplierName.trim() || null,
          supplier_sku: form.supplierSku.trim() || null,
          supplier_url: form.supplierUrl.trim() || null,
          cost_minor: costMinorVal,
        };
        await createPosProduct(input);
      }
      onSaved();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const issues = (err.body as { issues?: unknown } | null)?.issues;
        if (Array.isArray(issues) && issues.every((s) => typeof s === 'string')) {
          setServerIssues(issues as string[]);
        } else {
          setError(err.code);
        }
      } else {
        setError('Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      aria-label={existing ? 'Edit product' : 'New product'}
      maxWidth="max-w-md"
    >
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">
            {existing ? 'Edit product' : 'New product'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label
              htmlFor="pp-name"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Name <span className="text-destructive">*</span>
            </label>
            <input
              id="pp-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Dog Lead"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="pp-description"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Description (optional)
            </label>
            <textarea
              id="pp-description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Brief description shown at checkout"
              rows={2}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* Price + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="pp-price"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Price ({sym})
              </label>
              <input
                id="pp-price"
                type="number"
                step="0.01"
                min="0.01"
                value={form.priceText}
                onChange={(e) => setForm((p) => ({ ...p, priceText: e.target.value }))}
                placeholder="19.99"
                className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label
                htmlFor="pp-category"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Category (optional)
              </label>
              <input
                id="pp-category"
                type="text"
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                placeholder="Accessories"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          {/* SKU + Position */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="pp-sku"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                SKU (optional)
              </label>
              <input
                id="pp-sku"
                type="text"
                value={form.sku}
                onChange={(e) => setForm((p) => ({ ...p, sku: e.target.value }))}
                placeholder="LEAD-001"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              {/* Drag-reorder deferred to v1.1 — position is an integer input for now. */}
              <label
                htmlFor="pp-position"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Position
              </label>
              <input
                id="pp-position"
                type="number"
                step="1"
                min="0"
                value={form.positionText}
                onChange={(e) => setForm((p) => ({ ...p, positionText: e.target.value }))}
                className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          {/* Flags */}
          <div className="space-y-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.taxable}
                onChange={(e) => setForm((p) => ({ ...p, taxable: e.target.checked }))}
                className="rounded border-border"
              />
              Taxable
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                className="rounded border-border"
              />
              Active
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.stockTrackingEnabled}
                onChange={(e) => setForm((p) => ({ ...p, stockTrackingEnabled: e.target.checked }))}
                className="rounded border-border"
              />
              Track stock
            </label>
          </div>

          {/* Stock fields (shown only when tracking is enabled) */}
          {form.stockTrackingEnabled && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Stock settings
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="pp-stock-qty"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Current qty
                  </label>
                  <input
                    id="pp-stock-qty"
                    type="number"
                    step="1"
                    value={form.stockQuantityText}
                    onChange={(e) => setForm((p) => ({ ...p, stockQuantityText: e.target.value }))}
                    className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="pp-cost"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Cost ({sym})
                  </label>
                  <input
                    id="pp-cost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.costText}
                    onChange={(e) => setForm((p) => ({ ...p, costText: e.target.value }))}
                    placeholder="0.00"
                    className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="pp-reorder-threshold"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Reorder at
                  </label>
                  <input
                    id="pp-reorder-threshold"
                    type="number"
                    step="1"
                    min="0"
                    value={form.reorderThresholdText}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, reorderThresholdText: e.target.value }))
                    }
                    className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="pp-reorder-qty"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Order qty
                  </label>
                  <input
                    id="pp-reorder-qty"
                    type="number"
                    step="1"
                    min="0"
                    value={form.reorderQuantityText}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, reorderQuantityText: e.target.value }))
                    }
                    className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="pp-supplier-name"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Supplier (optional)
                </label>
                <input
                  id="pp-supplier-name"
                  type="text"
                  value={form.supplierName}
                  onChange={(e) => setForm((p) => ({ ...p, supplierName: e.target.value }))}
                  placeholder="Dog Supplies Co."
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
          )}

          {/* Errors */}
          {error !== null && <p className="text-sm text-destructive">{error}</p>}
          {serverIssues !== null && (
            <ul className="space-y-1 text-sm text-destructive">
              {serverIssues.map((iss) => (
                <li key={iss}>• {iss}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save product'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ModalState = { kind: 'none' } | { kind: 'add' } | { kind: 'edit'; product: PosProduct };

export function PosProductsSettings() {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const currency = account?.currency ?? 'GBP';
  const sym = currencySymbol(currency);
  const { confirm, ConfirmDialog } = useConfirm();

  const [products, setProducts] = useState<PosProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const res = await listPosProducts(true);
      setProducts(res.products);
    } catch (err) {
      // 403 = missing pos.session.read capability; 402 = Starter tier (POS is Pro+).
      // Both cases: user cannot manage products, show the permission-denied banner.
      if (err instanceof ApiError && (err.status === 403 || err.status === 402)) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleDelete(product: PosProduct) {
    const confirmed = await confirm({
      title: `Deactivate "${product.name}"?`,
      message: 'The product will be hidden from the till. Historical transactions are preserved.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await deletePosProduct(product.id);
      void refresh();
    } catch {
      // Ignore — product will still appear in the list; user can retry.
    }
  }

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage POS products
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access, or upgrade to a Pro plan.
        </p>
      </div>
    );
  }

  const grouped = groupByCategory(products);

  return (
    <div className="space-y-6">
      <ConfirmDialog />

      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Retail products</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Flat catalogue for over-the-counter sales at your kennel. Products appear in the POS
            till and are grouped by category. Drag-reorder is coming in v1.1 — use the position
            field to control order within each group.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'add' })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          Create product
        </button>
      </header>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Package className="mx-auto mb-3 size-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium text-foreground">No retail products configured.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add a product to sell it at the POS till.
          </p>
          <button
            type="button"
            onClick={() => setModal({ kind: 'add' })}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            Create product
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {[...grouped.entries()].map(([category, group]) => (
            <section key={category}>
              <h2 className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                {category}
              </h2>
              <div className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
                <div className="grid grid-cols-[1.5fr_6rem_4rem_6rem_6rem_auto] gap-3 border-b border-border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  <span>Name</span>
                  <span>SKU</span>
                  <span>Pos</span>
                  <span>Price</span>
                  <span>Status</span>
                  <span />
                </div>
                <div className="divide-y divide-border">
                  {group.map((product) => (
                    <div
                      key={product.id}
                      className="grid grid-cols-[1.5fr_6rem_4rem_6rem_6rem_auto] items-center gap-3 px-6 py-3.5 text-sm"
                    >
                      <div>
                        <span className="font-medium">{product.name}</span>
                        {product.description && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {product.description}
                          </p>
                        )}
                      </div>
                      <span className="num text-muted-foreground">{product.sku ?? '—'}</span>
                      <span className="num text-muted-foreground">{product.position}</span>
                      <span className="num text-foreground">
                        {sym}
                        {minorToMajor(product.priceMinor)}
                      </span>
                      <span>
                        {product.active === 1 ? (
                          <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
                            Inactive
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'edit', product })}
                          className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
                          title="Edit product"
                        >
                          <Pencil className="size-3.5" strokeWidth={1.75} />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(product)}
                          className="rounded-md p-1 text-muted-foreground hover:text-destructive press"
                          title="Delete product"
                        >
                          <Trash2 className="size-3.5" strokeWidth={1.75} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ))}
        </div>
      )}

      {modal.kind === 'add' && (
        <EditorDrawer
          existing={null}
          currency={currency}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'edit' && (
        <EditorDrawer
          existing={modal.product}
          currency={currency}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
    </div>
  );
}
